import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classnames from 'classnames';

const VIEWPORT_PADDING = 12;
const TRIGGER_GAP = 6;
const ARROW_EDGE_PADDING = 8;

interface TooltipProps {
    content: string;
    children: React.ReactNode;
    isMobile: boolean;
    id?: string;
    className?: string;
    style?: React.CSSProperties;
    variant?: 'default' | 'overflow';
    shouldShow?: (triggerElement: HTMLElement) => boolean;
}

interface Position {
    top: number;
    left: number;
    arrowLeft: number;
}

export const InfoTooltip = ({ id, content, children, className, style, variant = 'default', shouldShow, isMobile }: TooltipProps) => {
    const triggerRef = useRef<HTMLSpanElement>(null);
    const popupRef = useRef<HTMLSpanElement>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [position, setPosition] = useState<Position | null>(null);

    const showTooltip = useCallback(() => {
        const element = triggerRef.current;
        if (isMobile || !element || (shouldShow && !shouldShow(element))) {
            return;
        }
        setIsVisible(true);
    }, [isMobile, shouldShow]);

    const hideTooltip = useCallback(() => {
        setIsVisible(false);
        setPosition(null);
    }, []);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const popup = popupRef.current;
        if (!trigger || !popup) {
            return;
        }

        const triggerRect = trigger.getBoundingClientRect();
        const popupRect = popup.getBoundingClientRect();

        // Position above the trigger.
        const top = triggerRect.top - popupRect.height - TRIGGER_GAP;

        // Center over the trigger, then clamp within the viewport.
        const triggerCenter = triggerRect.left + triggerRect.width / 2;
        const centeredLeft = triggerCenter - popupRect.width / 2;
        const maxLeft = Math.max(VIEWPORT_PADDING, window.innerWidth - popupRect.width - VIEWPORT_PADDING);
        const left = Math.min(Math.max(centeredLeft, VIEWPORT_PADDING), maxLeft);

        // Keep the arrow pointing at the trigger even after clamping.
        const arrowLeft = Math.min(Math.max(triggerCenter - left, ARROW_EDGE_PADDING), popupRect.width - ARROW_EDGE_PADDING);

        setPosition({ top, left, arrowLeft });
    }, []);

    // Recalculate after the popup renders (its measured width is needed for clamping),
    // and keep it aligned while visible on resize/scroll.
    useLayoutEffect(() => {
        if (!isVisible) {
            return;
        }
        updatePosition();

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
        };
    }, [isVisible, updatePosition]);

    return (
        <span
            ref={triggerRef}
            className={classnames('bs-info-tooltip', className, { 'bs-info-tooltip--overflow': variant === 'overflow' })}
            style={style}
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
            onFocus={showTooltip}
            onBlur={hideTooltip}
            aria-describedby={isVisible && id ? id : undefined}
        >
            {children}
            {isVisible &&
                typeof document !== 'undefined' &&
                createPortal(
                    <span
                        ref={popupRef}
                        id={id}
                        className='bs-info-tooltip__popup'
                        role='tooltip'
                        style={
                            position
                                ? {
                                      top: position.top,
                                      left: position.left,
                                      ['--tooltip-arrow-left' as string]: `${position.arrowLeft}px`
                                  }
                                : { visibility: 'hidden' }
                        }
                    >
                        {content}
                    </span>,
                    document.body
                )}
        </span>
    );
};
